import type { Variants, Transition } from 'motion/react';

/**
 * Shared animation variants for consistent motion across the app
 */

export const fadeIn: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export const fadeOut: Variants = {
  initial: { opacity: 1 },
  animate: { opacity: 0 },
  exit: { opacity: 1 },
};

export const slideUp: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

export const slideDown: Variants = {
  initial: { opacity: 0, y: -20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 20 },
};

export const scaleIn: Variants = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 },
};

export const scaleOut: Variants = {
  initial: { opacity: 1, scale: 1 },
  animate: { opacity: 0, scale: 0.95 },
  exit: { opacity: 1, scale: 1 },
};

export const pulse: Variants = {
  initial: { scale: 1 },
  animate: {
    scale: [1, 1.05, 1],
    transition: {
      duration: 0.6,
      repeat: Infinity,
      repeatType: 'reverse',
    },
  },
};

export const glow: Variants = {
  initial: { boxShadow: '0 0 0px rgba(0, 0, 0, 0)' },
  animate: {
    boxShadow: [
      '0 0 0px rgba(59, 130, 246, 0)',
      '0 0 20px rgba(59, 130, 246, 0.5)',
      '0 0 0px rgba(59, 130, 246, 0)',
    ],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

export const shake: Variants = {
  initial: { x: 0 },
  animate: {
    x: [0, -10, 10, -10, 10, 0],
    transition: {
      duration: 0.5,
    },
  },
};

export const bounce: Variants = {
  initial: { scale: 0.8, opacity: 0 },
  animate: {
    scale: [0.8, 1.1, 1],
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
};

export const celebration: Variants = {
  initial: { scale: 0.8, opacity: 0, rotate: -10 },
  animate: {
    scale: [0.8, 1.2, 1],
    opacity: 1,
    rotate: [0, 5, -5, 0],
    transition: {
      duration: 0.8,
      ease: 'easeOut',
    },
  },
};

/**
 * Common transition presets
 */
export const transitions = {
  smooth: {
    duration: 0.3,
    ease: [0.4, 0, 0.2, 1],
  } satisfies Transition,
  spring: {
    type: 'spring',
    stiffness: 300,
    damping: 30,
  } satisfies Transition,
  springGentle: {
    type: 'spring',
    stiffness: 200,
    damping: 25,
  } satisfies Transition,
  springBouncy: {
    type: 'spring',
    stiffness: 400,
    damping: 20,
  } satisfies Transition,
  quick: {
    duration: 0.2,
    ease: 'easeOut',
  } satisfies Transition,
  slow: {
    duration: 0.5,
    ease: 'easeInOut',
  } satisfies Transition,
} as const;

/**
 * Stagger configuration for list animations
 */
export const staggerConfig = {
  container: {
    staggerChildren: 0.05,
    delayChildren: 0.1,
  } satisfies Transition,
  fast: {
    staggerChildren: 0.03,
    delayChildren: 0.05,
  } satisfies Transition,
  slow: {
    staggerChildren: 0.1,
    delayChildren: 0.2,
  } satisfies Transition,
} as const;
